import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.jsx";
import { Button } from "@/components/ui/button.jsx";
import { useToast } from "@/hooks/use-toast.js";
import { FileText } from "lucide-react";
import { useCompanyDocuments } from "@/lib/hooks/useCompanies.js";
import BankStatements from "./documents/BankStatements.jsx";
import TaxClearances from "./documents/TaxClearances.jsx";
import { useBanks } from "@/lib/hooks/useBanks.js";

const CompanyDocuments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isExistingCompany, setIsExistingCompany] = useState(false);
  const [documentsCompleted, setDocumentsCompleted] = useState(false);
  const [activeTab, setActiveTab] = useState("statements");

  // Get company data from navigation state or URL params
  const companyId = location.state?.companyId || searchParams.get("companyId");
  const companyName = location.state?.companyName || "";
  const createdCompanyData = location.state?.createdCompanyData || null;

  // Use the company documents hook
  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    error: documentsError,
    refetch: refetchDocuments,
  } = useCompanyDocuments(companyId);

const { 
  banks, 
  refetch: refetchBanks 
} = useBanks(companyId);

  // Check if this is an existing company (accessed via URL params)
  useEffect(() => {
    if (searchParams.get("companyId")) {
      setIsExistingCompany(true);
    }
  }, [searchParams]);

  // Set initial tab based on URL parameter
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && (tabParam === "statements" || tabParam === "fiscal")) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Update documents completed status when documents data changes
  useEffect(() => {
    if (isExistingCompany && documentsData) {
      const { completed } = getTotalDocumentsCount();
      setDocumentsCompleted(completed >= 4); // Consider completed if at least 4 documents are processed
    }
  }, [documentsData, isExistingCompany]);

  // Helper function to calculate total documents count
  const getTotalDocumentsCount = () => {
    // 1. Safety check: ensure documentsData is an array
    if (!documentsData || !Array.isArray(documentsData) || documentsData.length === 0) {
      return { completed: 0, total: 0 };
    }

    // 2. In your new API structure, documentsData is the list of statements.
    // We count a document as 'completed' if its 'date' is not null 
    // (which implies OCR processing is finished).
    const total = documentsData.length;
    const completed = documentsData.filter(stmt => stmt.date !== null).length;

    return { completed, total };
  };

  const handleReturnToSocieties = () => {
    navigate("/societes");
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const handleRefresh = () => {
    refetchDocuments();
    refetchBanks();
  };

  const handleTaxClearanceRefresh = () => {
    // Optional: could trigger something else, but TaxClearances handles its own refresh now
    // Maybe just refetch main documents to update counts if needed
    refetchDocuments();
  };

  if (!companyId) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-destructive mb-4">Erreur</h1>
            <p className="text-muted-foreground mb-4">
              Aucun ID d'entreprise fourni
            </p>
            <Button onClick={() => navigate("/societes")}>
              Retour aux sociétés
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Documents de l'entreprise</h1>
          {companyName && (
            <p className="text-muted-foreground mt-1">{companyName}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReturnToSocieties}>
            Retour aux sociétés
          </Button>
          <Button
            onClick={() => {
              navigate(`/etude?companyId=${companyId}`);
            }}
          >
            <FileText className="h-4 w-4 mr-2" />
            Voir étude
          </Button>
        </div>
      </div>

      {/* Document status display */}
      {isExistingCompany && documentsData && (
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Statut des documents</h3>
            <div className="text-sm text-muted-foreground">
              {(() => {
                const { completed, total } = getTotalDocumentsCount();
                return `${completed}/${total} documents traités`;
              })()}
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="statements">
                Relevés bancaires (6 derniers mois)
              </TabsTrigger>
              <TabsTrigger value="fiscal">Liasse fiscale</TabsTrigger>
            </TabsList>

            <TabsContent value="statements" className="space-y-4 mt-4">
              <BankStatements
                companyId={companyId}
                documentsData={documentsData}
                isLoadingDocuments={isLoadingDocuments}
                documentsError={documentsError}
                onRefresh={handleRefresh}
                banks={banks}
              />
            </TabsContent>

            <TabsContent value="fiscal" className="space-y-4 mt-4">
              <TaxClearances
                companyId={companyId}
                onRefresh={handleTaxClearanceRefresh}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Company information display */}
      {createdCompanyData && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Informations de l'entreprise
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium">
                  Nom de l'entreprise
                </Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {createdCompanyData?.name || "Non disponible"}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">SIRET</Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {createdCompanyData?.siret || "Non disponible"}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Date de création</Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {createdCompanyData?.immatriculation_date
                    ? new Date(
                        createdCompanyData.immatriculation_date,
                      ).toLocaleDateString("fr-FR")
                    : "Non disponible"}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Code APE</Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {createdCompanyData?.activity_code || "Non disponible"}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">
                  Secteur d'activité
                </Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {createdCompanyData?.activities_sector || "Non disponible"}
                </div>
              </div>
              <div className="space-y-1 col-span-1">
                <Label className="text-xs font-medium">Adresse</Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {createdCompanyData?.address}
                  <br />
                  {createdCompanyData?.zip_code} {createdCompanyData?.town}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompanyDocuments;
